/**
 * Size Guide Modal Web Component
 *
 * Features:
 * - Accessible modal with focus trap
 * - Tab switching for fit types
 * - Unit toggle (inches/cm) with localStorage persistence
 * - Keyboard navigation (Escape to close)
 * - Click outside to close
 */

if (!customElements.get('size-guide-modal')) {
  class SizeGuideModal extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('.size-guide-modal__close');
      this.fitTabs = this.querySelectorAll('.size-guide__fit-tab');
      this.fitPanels = this.querySelectorAll('.size-guide__panel');
      this.unitButtons = this.querySelectorAll('.size-guide__unit-btn');

      this.previouslyFocused = null;

      this.bindEvents();
      this.restoreUnitPreference();
    }

    bindEvents() {
      // Close button
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.hide());
      }

      // Click outside modal content
      this.addEventListener('click', (e) => {
        if (e.target === this) this.hide();
      });

      // Escape key
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hide();
      });

      // Fit tabs
      this.fitTabs.forEach(tab => {
        tab.addEventListener('click', () => this.switchFit(tab.dataset.fit));
      });

      // Unit toggles (handle all panels)
      this.unitButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const panel = e.target.closest('.size-guide__panel');
          this.switchUnit(btn.dataset.unit, panel);
        });
      });
    }

    show(opener) {
      this.previouslyFocused = opener || document.activeElement;

      this.setAttribute('open', '');
      this.removeAttribute('aria-hidden');
      document.body.classList.add('overflow-hidden');

      // Focus the dialog
      if (this.dialog) {
        this.dialog.focus();
      }

      // Trap focus
      this.trapFocus();
    }

    hide() {
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');

      // Restore focus
      if (this.previouslyFocused) {
        this.previouslyFocused.focus();
      }

      this.releaseFocus();
    }

    switchFit(fit) {
      // Update tabs
      this.fitTabs.forEach(tab => {
        const isSelected = tab.dataset.fit === fit;
        tab.setAttribute('aria-selected', isSelected);
      });

      // Update panels
      this.fitPanels.forEach(panel => {
        const isActive = panel.dataset.fit === fit;
        panel.hidden = !isActive;
      });
    }

    switchUnit(unit, panel) {
      if (!panel) return;

      // Update buttons in this panel
      const buttons = panel.querySelectorAll('.size-guide__unit-btn');
      buttons.forEach(btn => {
        btn.setAttribute('aria-pressed', btn.dataset.unit === unit);
      });

      // Update table visibility in this panel
      const tables = panel.querySelectorAll('.size-guide__table-container');
      tables.forEach(table => {
        table.hidden = table.dataset.unit !== unit;
      });

      // Persist preference
      this.saveUnitPreference(unit);
    }

    saveUnitPreference(unit) {
      try {
        localStorage.setItem('punny-size-guide-unit', unit);
      } catch (e) {
        // localStorage not available
      }
    }

    restoreUnitPreference() {
      try {
        const savedUnit = localStorage.getItem('punny-size-guide-unit');
        if (savedUnit) {
          this.fitPanels.forEach(panel => {
            this.switchUnit(savedUnit, panel);
          });
        }
      } catch (e) {
        // localStorage not available
      }
    }

    trapFocus() {
      if (!this.dialog) return;

      const focusableElements = this.dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      this.handleFocusTrap = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      this.dialog.addEventListener('keydown', this.handleFocusTrap);
    }

    releaseFocus() {
      if (this.handleFocusTrap && this.dialog) {
        this.dialog.removeEventListener('keydown', this.handleFocusTrap);
      }
    }
  }

  customElements.define('size-guide-modal', SizeGuideModal);
}

// Size Guide Link opener
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.size-guide-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const modalId = link.dataset.modalTarget;
      const modal = document.getElementById(modalId);
      if (modal && modal.show) {
        modal.show(link);
      }
    });
  });
});
